﻿import PropTypes from "prop-types";
import React, { Fragment, useState, useEffect } from "react";
import { QuestionBreadcrumb } from "../shared/question-breadcrumb";
import { StatusPanel } from "components/status";
import { DateToLocal } from "components/dates";
import { getQuestionInfo } from "../../utils";
import { MediaList } from "components/media";
import { UserAccess } from "components/user-access";
import { ShowHide } from "components/layout";
import { NotificationInline, NotificationToaster } from "components/notifications";
import { Intent } from '@blueprintjs/core';
import { Button } from "components/buttons";
import { Tooltip } from "components/tooltip";
import { DefectSignoffModal } from 'pages/defects/shared';
import { CheckService } from 'services/CheckService';
import classNames from 'classnames';

export function CheckDefectSummary(props) {
    const { defect, defectNumber, templateLayout, loading } = props;
    const [localDefect, setLocalDefect] = useState(defect);
    const [showDefectSignoffModal, setShowDefectSignoffModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const signOffRequired = localDefect.signOffDate == null;
    const status = signOffRequired ? "unresolved" : "resolved";
    const questionInfo = getQuestionInfo(templateLayout, localDefect.questionId);
    const fontSize = "1em";
    const fontWeight = "normal";

    useEffect(() => {
        setLocalDefect(defect);
    }, [defect]);

    function signOffDefect(defectSignOff) {
        setSaving(true);

        CheckService.signOffDefect(props.checkId, defectSignOff.defectId, defectSignOff).then(
            (reponse) => {
                NotificationToaster.show(Intent.SUCCESS, "Defect signed off  successfully.");
                setSaving(false);
                const updatedDefect = {
                    ...defect,
                    ...defectSignOff
                };
                setLocalDefect(updatedDefect);
                setShowDefectSignoffModal(false);
                props.onDefectSignedOff(updatedDefect);
            })
            .catch((error) => {
                NotificationToaster.show(Intent.DANGER, "Failed to sign off defect. Please try again later.");
            })
            .finally(() => {
                setSaving(false);
            });
    }

    const totalMedia = Array.isArray(localDefect?.media) ? localDefect.media.length + (localDefect.numberOfMissingMedia || 0) : 0;

    const RetentionPolicyNotification = ({media}) => {
        if (!media || media.length === 0) return null;

        return (
            <NotificationInline
                allowClose={false}
                isShort={true}
                show={media.filter(m => m.retentionPolicyApplied).length > 0}
                text="Media has been removed from this question as it exceeds the Logistics UK retention policy."
                intent="danger"
                additionalStyle={{ marginBottom: '10px' }}
            />
        );
    };

    return (
        <Fragment>
            <StatusPanel
                title={`Reported Defect ${defectNumber}`}
                status={status}
                loading={loading}
            >
                <QuestionBreadcrumb templateLayout={templateLayout} questionId={localDefect.questionId} loading={loading} />
                
                <div className={classNames({ "bp6-skeleton": loading })}>
                    <h4>Question {questionInfo.questionNumber}. <span style={{ fontSize, fontWeight }}>{questionInfo.questionTitle}</span></h4>
                </div>

                <div className={classNames({ "bp6-skeleton": loading })}>
                    <h4 >Defect Comment: <span style={{ fontSize, fontWeight }}>{localDefect?.defectComment}</span></h4>
                </div>

                {Array.isArray(localDefect?.media) && localDefect.media.length > 0 && (
                    <MediaList loading={loading} media={localDefect.media} mediaTitle="Media" />
                )}

                <RetentionPolicyNotification media={localDefect?.media} />

                <ShowHide
                    evaluator={localDefect.numberOfMissingMedia > 0}
                    show={<div className="check-fullresults-defect-missingmedia">Media Pending ({localDefect.numberOfMissingMedia}/{totalMedia})</div>}
                />

                <ShowHide
                    evaluator={!signOffRequired}
                    show={
                        <NotificationInline
                            allowClose={false}
                            show
                            text={<div>Defect signed off by {localDefect.signOffUser} on <DateToLocal format='DD/MM/YYYY HH:mm'>{localDefect.signOffDate}</DateToLocal></div>}
                            intent="success"
                            isShort={true}
                            additionalStyle={{ marginBottom: '10px' }}
                        ></NotificationInline>
                    }
                />

                <UserAccess
                    perform={["Checks:SignOff"]}
                    yes={() => (
                        <ShowHide
                            evaluator={signOffRequired}
                            show={
                                <Tooltip content={"Sign off the defect"}>
                                    <div style={{ marginTop: '5px' }}>
                                        <Button
                                            intent="primary"
                                            text="SIGN OFF DEFECT"
                                            id="button-defect-sign-off"
                                            loading={loading}
                                            onClick={() => setShowDefectSignoffModal(true)}
                                            icon="tick"
                                            large={false}
                                            disabled={loading}
                                        />
                                    </div>
                                </Tooltip>
                            }
                        />
                    )}
                />
            </StatusPanel>

            <DefectSignoffModal
                title="Sign Off Defect"
                isOpen={showDefectSignoffModal}
                loading={loading}
                defect={localDefect}
                onClose={() => setShowDefectSignoffModal(false)}
                signOffDefect={signOffDefect}
                saving={saving}
                UserAccessActions={["Checks:SignOff"]}
            />            
        </Fragment>
    );
}

CheckDefectSummary.propTypes = {
    defect: PropTypes.object.isRequired,
    defectNumber: PropTypes.number.isRequired,
    templateLayout: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired,
    onDefectSignedOff: PropTypes.func.isRequired,
    checkId: PropTypes.number.isRequired
};